import { createContext, useContext, type PropsWithChildren } from 'react';
import { useAuth, useUser } from '@clerk/tanstack-react-start';
import { getRoleFromMetadata, type AppRole } from '../lib/auth/roles';

export type AppAuthState = {
  clerkEnabled: boolean;
  isLoaded: boolean;
  isSignedIn: boolean;
  role: AppRole | null;
};

const defaultState: AppAuthState = {
  clerkEnabled: false,
  isLoaded: true,
  isSignedIn: false,
  role: null,
};

const AppAuthContext = createContext<AppAuthState>(defaultState);

/** Provides role state when Clerk is not configured. */
export function DisabledAppAuthProvider({ children }: PropsWithChildren) {
  return <AppAuthContext.Provider value={defaultState}>{children}</AppAuthContext.Provider>;
}

/** Must render inside ClerkProvider. */
export function ClerkAppAuthProvider({ children }: PropsWithChildren) {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const role = getRoleFromMetadata(
    user?.publicMetadata as Record<string, unknown> | undefined,
    user?.unsafeMetadata as Record<string, unknown> | undefined
  );

  const value: AppAuthState = {
    clerkEnabled: true,
    isLoaded,
    isSignedIn: Boolean(isSignedIn),
    role,
  };

  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
}

export function useAppRole(): AppAuthState {
  return useContext(AppAuthContext);
}
